/* Color Grid Popup Module v2.0.0 - ported from live (comments stripped, logic unchanged). Exposes window.ColorGridPopup; reads window.structuredColors (set by vectary-scripts.js). */
(function(window, document) {
  'use strict';

  const CONFIG = {
    MODAL_ID: 'colorGridModal',
    BUTTON_CLASS: 'color-grid-popup-btn',
    SEARCH_DEBOUNCE: 300,
    VIRTUAL_SCROLL_THRESHOLD: 100,
    ANIMATIONS_ENABLED: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    CATEGORIES: ['Black & Gray', 'Red & Pink', 'Orange', 'Yellow & Gold', 'Green', 'Blue & Teal', 'Brown & Tan', 'Purple']
  };

  const SELECTORS = {
    modal: `#${CONFIG.MODAL_ID}`,
    content: '.color-grid-content',
    close: '.color-grid-close',
    search: '.color-grid-search',
    categories: '.color-grid-categories',
    categoryTemplate: '.color-category',
    swatchTemplate: '.color-swatch-item'
  };

  class ColorCategorizer {
    constructor() {
      this.colorKeywords = {
        'Black & Gray': ['black', 'grey', 'gray', 'charcoal', 'steel', 'platinum', 'titanium', 'silver', 'white', 'catalyst', 'eclipse'],
        'Red & Pink': ['red', 'pink', 'magenta', 'rhodamine', 'pepper', 'spartan', 'lava', 'chile', 'honda', 'trixx'],
        'Orange': ['orange', 'blaze', 'phoenix', 'crush', 'ktm', 'burst'],
        'Yellow & Gold': ['yellow', 'gold', 'sunburst', 'millennium', 'vintage', 'ski-doo', 'lime', 'nuclear', 'dayglow', 'neo'],
        'Green': ['green', 'mint', 'manta', 'quetzal', 'racing', 'arctic cat green', 'army'],
        'Blue & Teal': ['blue', 'teal', 'cyan', 'turquoise', 'azure', 'navy', 'oxford', 'belize', 'iceberg', 'reef', 'vapor', 'dazzling', 'dusty', 'labrador', 'panama', 'gulfstream', 'caribbean', 'octane', 'scandi', 'vintage polaris', 'vintage ski-doo blue'],
        'Brown & Tan': ['brown', 'tan', 'bronze', 'desert', 'arctic tan', 'liquid', 'polaris glow'],
        'Purple': ['purple', 'violet', 'midnight']
      };
    }

    categorize(colorsArray) {
      const categories = {};
      CONFIG.CATEGORIES.forEach(category => { categories[category] = []; });
      colorsArray.forEach((color, index) => {
        const category = this.determineCategory(color);
        categories[category].push({ ...color, swatchIndex: index + 1, searchText: this.createSearchText(color), category: category });
      });
      Object.keys(categories).forEach(category => {
        categories[category].sort((a, b) => a.name.localeCompare(b.name));
      });
      return categories;
    }

    determineCategory(color) {
      const name = color.name.toLowerCase();
      const hex = color.hex.toLowerCase();
      let bestCategory = null;
      let bestKeywordLength = 0;
      for (const [category, keywords] of Object.entries(this.colorKeywords)) {
        for (const keyword of keywords) {
          if (name.includes(keyword) && keyword.length > bestKeywordLength) {
            bestKeywordLength = keyword.length;
            bestCategory = category;
          }
        }
      }
      if (bestCategory) return bestCategory;
      return this.categorizeByHex(hex);
    }

    categorizeByHex(hex) {
      const rgb = this.hexToRgb(hex);
      if (!rgb) return 'Black & Gray';
      const [r, g, b] = rgb;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const saturation = max === 0 ? 0 : (max - min) / max;
      if (saturation < 0.15) return 'Black & Gray';
      if (r > g && r > b) {
        if (g > 150 && r > 200) return 'Yellow & Gold';
        if (g > 100 && b < 100) return 'Orange';
        return 'Red & Pink';
      }
      if (g > r && g > b) {
        if (r > 180 && g > 180) return 'Yellow & Gold';
        return 'Green';
      }
      if (b > r && b > g) {
        if (g > 100) return 'Blue & Teal';
        if (r > 100) return 'Purple';
        return 'Blue & Teal';
      }
      return 'Black & Gray';
    }

    hexToRgb(hex) {
      const cleanHex = hex.replace('#', '');
      if (cleanHex.length !== 6) return null;
      return [parseInt(cleanHex.substring(0, 2), 16), parseInt(cleanHex.substring(2, 4), 16), parseInt(cleanHex.substring(4, 6), 16)];
    }

    createSearchText(color) {
      return `${color.name} ${color.hex}`.toLowerCase();
    }
  }

  class PerformanceUtils {
    static debounce(func, delay) {
      let timeoutId;
      return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    }
    static throttle(func, delay) {
      let lastCall = 0;
      return function(...args) {
        const now = Date.now();
        if (now - lastCall >= delay) { lastCall = now; func.apply(this, args); }
      };
    }
    static requestFrame(callback) {
      if (window.requestAnimationFrame) window.requestAnimationFrame(callback);
      else setTimeout(callback, 16);
    }
  }

  class AccessibilityManager {
    constructor(modal) {
      this.modal = modal;
      this.focusableElements = [];
      this.lastFocusedElement = null;
    }
    setup() {
      this.setupARIA();
      this.setupKeyboardNavigation();
      this.setupFocusManagement();
    }
    setupARIA() {
      const modal = this.modal;
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'color-grid-title');
      const content = modal.querySelector(SELECTORS.content);
      if (content) content.setAttribute('role', 'document');
    }
    setupKeyboardNavigation() {
      document.addEventListener('keydown', this.handleKeyDown.bind(this));
    }
    handleKeyDown(event) {
      if (!this.modal.classList.contains('active')) return;
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          window.ColorGridPopup.close();
          break;
        case 'Tab':
          this.handleTabNavigation(event);
          break;
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
          this.handleArrowNavigation(event);
          break;
      }
    }
    handleTabNavigation(event) {
      const focusableElements = this.getFocusableElements();
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === firstElement) { event.preventDefault(); lastElement.focus(); }
      } else {
        if (document.activeElement === lastElement) { event.preventDefault(); firstElement.focus(); }
      }
    }
    handleArrowNavigation(event) {
      const swatches = Array.from(this.modal.querySelectorAll('.color-swatch-item:not([style*="display: none"])'));
      const currentIndex = swatches.indexOf(document.activeElement);
      if (currentIndex === -1) return;
      let nextIndex;
      const gridWidth = this.calculateGridWidth();
      switch (event.key) {
        case 'ArrowUp': nextIndex = currentIndex - gridWidth; break;
        case 'ArrowDown': nextIndex = currentIndex + gridWidth; break;
        case 'ArrowLeft': nextIndex = currentIndex - 1; break;
        case 'ArrowRight': nextIndex = currentIndex + 1; break;
      }
      if (nextIndex >= 0 && nextIndex < swatches.length) {
        event.preventDefault();
        swatches[nextIndex].focus();
      }
    }
    calculateGridWidth() {
      const grid = this.modal.querySelector('.color-swatches-grid');
      if (!grid) return 1;
      const columns = window.getComputedStyle(grid).gridTemplateColumns.split(' ').length;
      return columns || 1;
    }
    getFocusableElements() {
      const selectors = ['button:not([disabled])', 'input:not([disabled])', '[tabindex]:not([tabindex="-1"])', 'a[href]'].join(', ');
      return Array.from(this.modal.querySelectorAll(selectors));
    }
    setupFocusManagement() {
      document.addEventListener('focus', (event) => {
        if (!this.modal.contains(event.target) && this.modal.classList.contains('active')) {
          this.lastFocusedElement = event.target;
        }
      }, true);
    }
    focusFirst() {
      const searchInput = this.modal.querySelector(SELECTORS.search);
      if (searchInput) searchInput.focus();
      else {
        const firstFocusable = this.getFocusableElements()[0];
        if (firstFocusable) firstFocusable.focus();
      }
    }
    restoreFocus() {
      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
    }
  }

  class ColorGridPopup {
    constructor() {
      this.modal = null;
      this.categorizer = new ColorCategorizer();
      this.accessibilityManager = null;
      this.currentSlider = null;
      this.categorizedColors = null;
      this.isInitialized = false;
      this.handleSearch = PerformanceUtils.debounce(this.handleSearch.bind(this), CONFIG.SEARCH_DEBOUNCE);
      this.handleSwatchClick = this.handleSwatchClick.bind(this);
      this.handleModalClick = this.handleModalClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
    }

    async init() {
      if (this.isInitialized) return;
      try {
        await this.createModal();
        await this.prepareColors();
        this.setupEventListeners();
        this.setupAccessibility();
        this.isInitialized = true;
      } catch (error) {
        console.error('Failed to initialize Color Grid Popup:', error);
      }
    }

    async createModal() {
      const existingModal = document.getElementById(CONFIG.MODAL_ID);
      if (existingModal) existingModal.remove();
      const modalHTML = `
        <div id="${CONFIG.MODAL_ID}" class="color-grid-modal" aria-hidden="true">
          <div class="color-grid-content">
            <div class="color-grid-header">
              <h3 id="color-grid-title">Select Color</h3>
              <button class="color-grid-close" aria-label="Close color picker">&times;</button>
            </div>
            <div class="color-grid-search-container">
              <input type="text" class="color-grid-search" placeholder="Search colors..." aria-label="Search colors" />
            </div>
            <div class="color-grid-categories">
              <div class="color-grid-loading"><div class="color-grid-loading-text">Loading amazing colors...</div></div>
            </div>
          </div>
        </div>`;
      document.body.insertAdjacentHTML('beforeend', modalHTML);
      this.modal = document.getElementById(CONFIG.MODAL_ID);
    }

    async prepareColors() {
      if (typeof window.structuredColors === 'undefined') {
        console.warn('Color data not available yet. Waiting for data...');
        let attempts = 0;
        const maxAttempts = 50;
        while (typeof window.structuredColors === 'undefined' && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        if (typeof window.structuredColors === 'undefined') {
          throw new Error('Color data not available. Make sure structuredColors is defined.');
        }
      }
      return new Promise((resolve) => {
        PerformanceUtils.requestFrame(() => {
          this.categorizedColors = this.categorizer.categorize(window.structuredColors);
          resolve();
        });
      });
    }

    async renderColors() {
      const categoriesContainer = this.modal.querySelector('.color-grid-categories');
      categoriesContainer.innerHTML = '';
      if (!this.categorizedColors || Object.keys(this.categorizedColors).length === 0) {
        categoriesContainer.innerHTML = '<div class="color-grid-empty"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="M21 21l-4.35-4.35"></path></svg><p>No colors available</p></div>';
        return;
      }
      for (const [categoryName, colors] of Object.entries(this.categorizedColors)) {
        if (colors.length === 0) continue;
        categoriesContainer.appendChild(this.createCategoryElement(categoryName, colors));
      }
    }

    createCategoryElement(categoryName, colors) {
      const categoryDiv = document.createElement('div');
      categoryDiv.className = 'color-category';
      categoryDiv.setAttribute('data-category', categoryName.toLowerCase().replace(/\s+/g, '-'));
      const titleDiv = document.createElement('div');
      titleDiv.className = 'color-category-title';
      titleDiv.textContent = categoryName;
      const gridDiv = document.createElement('div');
      gridDiv.className = 'color-swatches-grid';
      colors.forEach(color => gridDiv.appendChild(this.createSwatchElement(color)));
      categoryDiv.appendChild(titleDiv);
      categoryDiv.appendChild(gridDiv);
      return categoryDiv;
    }

    createSwatchElement(color) {
      const swatchDiv = document.createElement('div');
      swatchDiv.className = 'color-swatch-item';
      swatchDiv.setAttribute('data-color-index', color.swatchIndex);
      swatchDiv.setAttribute('data-search-text', color.searchText);
      swatchDiv.setAttribute('tabindex', '0');
      swatchDiv.setAttribute('role', 'button');
      swatchDiv.setAttribute('aria-label', `Select ${color.name}`);
      const colorBox = document.createElement('div');
      colorBox.className = 'color-swatch-box';
      colorBox.style.backgroundColor = color.hex;
      const nameDiv = document.createElement('div');
      nameDiv.className = 'color-swatch-name';
      nameDiv.textContent = color.name.length > 18 ? color.name.substring(0, 16).trim() + '...' : color.name;
      nameDiv.setAttribute('title', color.name);
      const tooltip = document.createElement('div');
      tooltip.className = 'color-swatch-tooltip';
      tooltip.textContent = `${color.name} (${color.hex.toUpperCase()})`;
      swatchDiv.appendChild(colorBox);
      swatchDiv.appendChild(nameDiv);
      swatchDiv.appendChild(tooltip);
      swatchDiv.addEventListener('click', this.handleSwatchClick);
      swatchDiv.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.handleSwatchClick(event);
        }
      });
      swatchDiv.addEventListener('touchstart', () => {
        swatchDiv.style.transform = 'translateY(-2px) scale(0.98)';
        swatchDiv.style.transition = 'transform 0.1s ease';
      });
      swatchDiv.addEventListener('touchend', () => {
        setTimeout(() => {
          swatchDiv.style.transform = '';
          swatchDiv.style.transition = 'var(--color-grid-transition)';
        }, 100);
      });
      return swatchDiv;
    }

    setupEventListeners() {
      const searchInput = this.modal.querySelector(SELECTORS.search);
      if (searchInput) {
        searchInput.addEventListener('input', this.handleSearch);
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) searchInput.style.fontSize = '16px';
      }
      const closeButton = this.modal.querySelector(SELECTORS.close);
      if (closeButton) {
        closeButton.addEventListener('click', this.handleCloseClick);
        closeButton.addEventListener('touchstart', () => { closeButton.style.transform = 'scale(0.95)'; });
        closeButton.addEventListener('touchend', () => { closeButton.style.transform = ''; });
      }
      this.modal.addEventListener('click', this.handleModalClick);
      this.modal.addEventListener('touchstart', (e) => {
        if (e.target === this.modal) e.preventDefault();
      }, { passive: false });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.modal.classList.contains('active')) this.close();
      });
    }

    setupAccessibility() {
      this.accessibilityManager = new AccessibilityManager(this.modal);
      this.accessibilityManager.setup();
    }

    handleSearch(event) {
      const searchTerm = event.target.value.toLowerCase().trim();
      const categories = this.modal.querySelectorAll('.color-category');
      categories.forEach(category => {
        const swatches = category.querySelectorAll('.color-swatch-item');
        let hasVisibleSwatches = false;
        swatches.forEach(swatch => {
          const searchText = swatch.getAttribute('data-search-text');
          const isVisible = !searchTerm || searchText.includes(searchTerm);
          swatch.style.display = isVisible ? 'flex' : 'none';
          if (isVisible) hasVisibleSwatches = true;
        });
        category.style.display = hasVisibleSwatches ? 'block' : 'none';
      });
      const hasAnyVisible = Array.from(categories).some(cat => cat.style.display !== 'none');
      if (!hasAnyVisible && searchTerm) this.showEmptyState('No colors found matching your search.');
      else this.hideEmptyState();
    }

    handleSwatchClick(event) {
      const swatchElement = event.currentTarget;
      const swatchIndex = swatchElement.getAttribute('data-color-index');
      if (swatchIndex && this.currentSlider) {
        swatchElement.style.transform = 'translateY(-4px) scale(1.1)';
        swatchElement.style.boxShadow = '0 12px 30px rgba(0, 123, 255, 0.3)';
        setTimeout(() => {
          swatchElement.style.transform = '';
          swatchElement.style.boxShadow = '';
          swatchElement.style.background = '';
          swatchElement.style.borderColor = '';
          swatchElement.blur();
          this.selectColor(parseInt(swatchIndex, 10));
          this.close();
        }, 150);
      }
    }

    handleModalClick(event) {
      if (event.target === this.modal) this.close();
    }

    handleCloseClick(event) {
      event.preventDefault();
      this.close();
    }

    selectColor(swatchIndex) {
      if (!this.currentSlider) return;
      const slider = document.getElementById(this.currentSlider);
      if (!slider) return;
      slider.value = swatchIndex;
      slider.dispatchEvent(new Event('input', { bubbles: true }));
      if (typeof window.state !== 'undefined' && window.state.activePreset !== null) {
        window.state.activePreset = null;
        if (typeof window.updatePresetButtons === 'function') window.updatePresetButtons();
      }
      this.showSuccessFeedback(swatchIndex);
    }

    showSuccessFeedback(swatchIndex) {
      let colorName = 'Color';
      if (this.categorizedColors && window.structuredColors) {
        const color = window.structuredColors.find(c => (c.swatchIndex || (window.structuredColors.indexOf(c) + 1)) === swatchIndex);
        if (color) colorName = color.name;
      }
      const successEl = document.createElement('div');
      successEl.style.cssText = 'position:fixed;top:20px;right:20px;background:#10b981;color:white;padding:12px 16px;border-radius:8px;font-weight:500;z-index:10002;transform:translateX(100%);transition:transform 0.3s ease;box-shadow:0 8px 25px rgba(16,185,129,0.3);';
      successEl.textContent = `${colorName} selected!`;
      document.body.appendChild(successEl);
      setTimeout(() => { successEl.style.transform = 'translateX(0)'; }, 10);
      setTimeout(() => {
        successEl.style.transform = 'translateX(100%)';
        setTimeout(() => { if (successEl.parentNode) successEl.parentNode.removeChild(successEl); }, 300);
      }, 2000);
    }

    showEmptyState(message) {
      const categoriesContainer = this.modal.querySelector('.color-grid-categories');
      let emptyState = categoriesContainer.querySelector('.color-grid-empty');
      if (!emptyState) {
        emptyState = document.createElement('div');
        emptyState.className = 'color-grid-empty';
        emptyState.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="M21 21l-4.35-4.35"></path></svg><p></p>';
        emptyState.querySelector('p').textContent = message;
        categoriesContainer.appendChild(emptyState);
      } else {
        emptyState.querySelector('p').textContent = message;
        emptyState.style.display = 'flex';
      }
    }

    hideEmptyState() {
      const emptyState = this.modal.querySelector('.color-grid-empty');
      if (emptyState) emptyState.style.display = 'none';
    }

    async open(sliderId) {
      if (!this.isInitialized) {
        try { await this.init(); } catch (error) { console.error('Failed to initialize color grid:', error); return; }
      }
      this.currentSlider = sliderId;
      this.modal.classList.add('active');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.top = `-${window.scrollY}px`;
      }
      if (!this.modal.querySelector('.color-category')) await this.renderColors();
      const searchInput = this.modal.querySelector(SELECTORS.search);
      if (searchInput) {
        searchInput.value = '';
        this.handleSearch({ target: searchInput });
      }
      if (this.accessibilityManager) this.accessibilityManager.focusFirst();
    }

    close() {
      if (!this.modal) return;
      this.modal.classList.remove('active');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        if (scrollY) window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
      if (this.accessibilityManager) this.accessibilityManager.restoreFocus();
      this.currentSlider = null;
    }

    destroy() {
      if (this.modal) this.modal.remove();
      this.isInitialized = false;
      this.currentSlider = null;
      this.categorizedColors = null;
    }
  }

  const colorGridPopup = new ColorGridPopup();

  window.ColorGridPopup = {
    open: (sliderId) => colorGridPopup.open(sliderId),
    close: () => colorGridPopup.close(),
    init: () => colorGridPopup.init(),
    destroy: () => colorGridPopup.destroy()
  };

  function autoInit() {
    setTimeout(() => {
      if (typeof window.structuredColors !== 'undefined') {
        colorGridPopup.init().catch(error => console.error('Failed to auto-initialize Color Grid Popup:', error));
      }
    }, 500);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
  else autoInit();

})(window, document);
